import { Download, FileQuestion, Upload } from 'lucide-react';
import { useCallback, useState } from 'react';

import { Button } from '../../../../components/Button';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { Skeleton } from '../../../../components/Skeleton';
import { FileField, USER_DOCUMENT_MAX_BYTES } from '../../../../components/inputs/FileField';
import { useBlobPreview } from '../../../../hooks/useBlobPreview';
import { ApiError } from '../../../../lib/apiError';
import { formatBytes } from '../../../../lib/formatBytes';
import { formatDateTime } from '../../../../lib/formatDate';
import { focusValidationError } from '../../../../lib/validationFocus';
import { nominationsApi, type AwardReadApi } from '../praiseApi';
import type { AwardNomineeDetail, AwardNomineeDocument } from '../praiseTypes';

const UPLOAD_DESCRIPTION =
  `The scanned document — PDF, PNG or JPEG, up to ${formatBytes(USER_DOCUMENT_MAX_BYTES)}. ` +
  "The type is verified against the file's own leading bytes rather than its name.";

/**
 * Submits the file for one checklist line, or replaces the one already there.
 *
 * **The line is the modal's subject, not a field** — the card opens this from its row. **No
 * Autogenerate**: the one field is a binary whose format the server verifies by magic bytes, and
 * there is no textual format to write honestly — the stated exemption.
 */
export function UploadNomineeDocumentModal({
  nominee,
  document,
  employeeName,
  onClose,
  onSaved,
}: {
  nominee: AwardNomineeDetail;
  document: AwardNomineeDocument;
  employeeName: string | null;
  onClose: () => void;
  onSaved: (saved: AwardNomineeDetail) => void;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const file = files[0] ?? null;
  const tooLarge = file !== null && file.size > USER_DOCUMENT_MAX_BYTES;
  const replacing = document.fileName != null;

  async function submit() {
    if (file === null || tooLarge) return;

    setSaving(true);
    setProgress(0);
    setError(null);

    try {
      onSaved(await nominationsApi.uploadDocument(nominee.id, document.id, file, setProgress));
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not submit that document.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'award-nominee-document' });
    } finally {
      setSaving(false);
      setProgress(null);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={replacing ? `Replace ${document.name}` : `Upload ${document.name}`}
      size="md"
      id="award-nominee-document-upload-modal"
      confirm={{ id: 'award-nominee-document-upload-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id="award-nominee-document-upload-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            id="award-nominee-document-upload-submit"
            onClick={() => void submit()}
            loading={saving}
            // Real upload progress — the client measures the bytes, so the label says how far it got.
            loadingLabel={progress === null ? 'Submitting' : `Filing ${progress}%`}
            disabled={file === null || tooLarge || !confirmed}
            leading={<Upload className="h-4 w-4" aria-hidden="true" />}
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <p role="alert" className="text-sm text-red-700">
            {error.message}
          </p>
        ) : null}

        {/* The CONSEQUENCE, on the form where it is read before the tick. */}
        <p className="text-sm text-slate-600">
          Submitted for <span className="font-medium text-slate-800">{document.name}</span>
          {employeeName ? (
            <>
              {' '}
              on <span className="font-medium text-slate-800">{employeeName}</span>&apos;s nomination
            </>
          ) : null}{' '}
          for {nominee.categoryName}
          {replacing ? (
            <>
              . It takes the place of <span className="font-medium text-slate-800">{document.fileName}</span> — the previous
              file is kept in the Repository, no longer listed here
            </>
          ) : null}
          .
        </p>

        <FileField
          id="award-nominee-document-file"
          label="Document"
          description={UPLOAD_DESCRIPTION}
          files={files}
          onChange={setFiles}
          required
          disabled={saving}
          error={error?.fieldError('file') ?? undefined}
        />
      </div>
    </Modal>
  );
}

/**
 * One submitted document, shown at the size the screen allows, beside what is known about it — the
 * attrition preview, for a nominee's line.
 *
 * **Everything security-bearing is `useBlobPreview`'s** — the VERIFIED type off the stored file, the
 * inert allow-list and the re-typed blob. The bytes come from the SURFACE's own download endpoint,
 * behind its read key and the employee's clearance. It writes nothing, so it carries no tick.
 */
export function NomineeDocumentPreviewModal({
  nominee,
  document,
  download,
  onDownload,
  onClose,
}: {
  nominee: AwardNomineeDetail;
  document: AwardNomineeDocument;
  download: AwardReadApi['downloadDocument'];
  onDownload: () => void;
  onClose: () => void;
}) {
  const load = useCallback(
    (signal: AbortSignal) => download(nominee.id, document.id, signal),
    [download, nominee.id, document.id],
  );

  const { kind, url, loading, error } = useBlobPreview(document.id, document.contentType ?? null, load);
  const fileName = document.fileName ?? document.name;

  return (
    <Modal
      open
      onClose={onClose}
      title={fileName}
      size="3xl"
      id="award-nominee-document-preview-modal"
      footer={
        <Button
          id="award-nominee-document-preview-download"
          variant="secondary"
          leading={<Download className="h-4 w-4" aria-hidden="true" />}
          onClick={onDownload}
          /* The only way to save the file once its name opens this, so it stays in the tab run. */
          tabIndex={0}
        >
          Download
        </Button>
      }
    >
      <div className="flex flex-col gap-4 lg:flex-row">
        <div className="min-w-0 flex-1">
          {kind === 'none' ? (
            <div className="flex min-h-[320px] flex-col items-center justify-center gap-4 rounded-lg bg-slate-50">
              <FileQuestion className="h-10 w-10 text-slate-300" aria-hidden="true" />
              <EmptyState minHeight="min-h-0" message="This file cannot be shown here. Download it to open it." />
            </div>
          ) : loading ? (
            <Skeleton className="h-[55vh] w-full" />
          ) : error || !url ? (
            <div className="flex min-h-[320px] items-center justify-center rounded-lg bg-slate-50">
              <ErrorState message={error?.message ?? 'Could not load the preview.'} />
            </div>
          ) : kind === 'image' ? (
            <div className="flex items-center justify-center rounded-lg bg-slate-100 p-3">
              <img src={url} alt={fileName} className="max-h-[55vh] w-auto max-w-full rounded object-contain" />
            </div>
          ) : (
            <iframe src={url} title={`Preview of ${fileName}`} className="h-[55vh] w-full rounded-lg border-0 bg-slate-50" />
          )}
        </div>

        <div className="w-full shrink-0 lg:w-80">
          <PreviewTable
            rows={[
              /* No File name row — the modal's TITLE is the file name, and a value is printed once. */
              { label: 'Document', value: document.name },
              { label: 'Type', value: document.contentType ?? '—', mono: document.contentType != null },
              { label: 'Size', value: document.sizeBytes != null ? formatBytes(document.sizeBytes) : '—', mono: document.sizeBytes != null },
              {
                label: 'Submitted at',
                value: document.submittedAt != null ? formatDateTime(document.submittedAt) : '—',
                mono: document.submittedAt != null,
              },
              { label: 'Submitted by', value: document.submittedByName ?? '—' },
            ]}
          />
        </div>
      </div>
    </Modal>
  );
}
